import {
  ShareMembershipInfoFragment,
  ShareListItemFragment,
  useShareUpdateMemberPermissionsMutation,
} from 'gqlTypes'
import { useCallback, useReducer, useEffect } from 'react'
import { ErrorHandler } from 'modules/tracing'
import { useToast } from 'containers/ToastManager'
import { useModalContext } from 'containers/ModalManager/ModalContext'

type PrivateAccessLevel = ShareMembershipInfoFragment['privateAccessLevel']

type Values = Pick<
  ShareMembershipInfoFragment,
  'privateAccessLevel' | 'commentsEnabled' | 'userCanUpdateCommentsEnabled'
>

type State = {
  initialValues: Values
  values: Values
  dialogOpen: boolean
  mutating: boolean
}

type Action =
  | { type: 'show-dialog' }
  | { type: 'hide-dialog' }
  | { type: 'set-mutating' }
  | { type: 'reset' }
  | { type: 'set-level'; payload: { level: PrivateAccessLevel } }
  | { type: 'set-comments-enabled'; payload: { enabled: boolean } }
  | { type: 'success'; payload: { values: Values } }

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'set-mutating':
      return { ...state, mutating: true }
    case 'show-dialog':
      return { ...state, dialogOpen: true }
    case 'hide-dialog':
      return { ...state, dialogOpen: false }
    case 'reset':
      return {
        ...state,
        dialogOpen: false,
        mutating: false,
        values: { ...state.initialValues },
      }
    case 'success':
      return {
        ...state,
        dialogOpen: false,
        mutating: false,
        values: action.payload.values,
        initialValues: action.payload.values,
      }
    case 'set-level':
      return {
        ...state,
        values: {
          ...state.values,
          privateAccessLevel: action.payload.level,
        },
      }
    case 'set-comments-enabled':
      return {
        ...state,
        values: {
          ...state.values,
          commentsEnabled: action.payload.enabled,
        },
      }
    default:
      return state
  }
}

const areValuesEqual = (a: Values, b: Values) =>
  a.privateAccessLevel === b.privateAccessLevel &&
  a.commentsEnabled === b.commentsEnabled

interface UseUpdateMemberProps {
  member: ShareMembershipInfoFragment
  share: Partial<ShareListItemFragment>
}

export const useUpdateMember = ({ share, member }: UseUpdateMemberProps) => {
  const { showToast } = useToast()
  const { hideModal } = useModalContext()
  const pending = member.inviteStatus === 'REQUESTED'

  const [
    { initialValues, values, dialogOpen, mutating },
    dispatch,
  ] = useReducer(reducer, {
    initialValues: member,
    values: member,
    dialogOpen: false,
    mutating: false,
  })

  const [
    updateMember,
    { loading: updateMemberLoading },
  ] = useShareUpdateMemberPermissionsMutation({
    onCompleted(data) {
      if (!data?.shareUpdateMemberPermissions) {
        ErrorHandler.shouldNeverHappen(
          'Share update member permissions mutation should return valid data'
        )
        dispatch({ type: 'reset' })
        return
      }
      if (
        data.shareUpdateMemberPermissions?.share?.userCanUpdateSettings ===
        false
      ) {
        hideModal()
        showToast("You can no longer view this document's settings")
        return
      }
      dispatch({
        type: 'success',
        payload: { values: data.shareUpdateMemberPermissions },
      })
    },
    onError(error) {
      if (error.includesErrorCode('CONFIRMATION_REQUIRED')) {
        dispatch({ type: 'show-dialog' })
        return
      }
      dispatch({ type: 'reset' })
      showToast(
        'There was a problem changing the member permissions. Please try again.',
        'negative'
      )
    },
  })

  const updateMemberConfirmed = useCallback(
    (confirmed: boolean = false) => {
      dispatch({ type: 'set-mutating' })
      return updateMember({
        variables: {
          identifier: member.identifier,
          permissions: {
            privateAccessLevel: values.privateAccessLevel,
            commentsEnabled: values.commentsEnabled,
            confirmed,
          },
        },
      })
    },
    [member, updateMember, values]
  )

  useEffect(() => {
    if (!mutating && !areValuesEqual(values, initialValues)) {
      updateMemberConfirmed()
    }
  }, [values, initialValues, updateMemberConfirmed, mutating])

  const getUpdateMemberConfirmDialogProps = useCallback(
    () => ({
      hideModal() {
        dispatch({ type: 'reset' })
      },
      onConfirm() {
        updateMemberConfirmed(true)
      },
      isModalOpen: true,
      confirmButton: {
        text: 'Confirm',
        intent: 'positive',
        loading: updateMemberLoading,
      } as const,
    }),
    [dispatch, updateMemberConfirmed, updateMemberLoading]
  )

  const getUpdateMemberDropdownProps = useCallback(
    () => ({
      access: {
        kind: 'private',
        level: pending ? 'PENDING' : initialValues.privateAccessLevel,
        setLevel: (level: PrivateAccessLevel) => {
          if (level === values.privateAccessLevel && pending) {
            // Handle the case where a PENDING member (inviteStatus REQUESTED)
            // has their access level re-set to the same value it currently is.
            // In this case we still need to manually trigger the mutation so
            // the BE auto-accepts their request, despite the values and
            // initialValues still being equal
            updateMemberConfirmed()
          } else {
            dispatch({ type: 'set-level', payload: { level } })
          }
        },
        allowedLevels: share.allowedPrivateAccessLevels || [],
      } as const,
      commentsEnabled: values.commentsEnabled,
      setCommentsEnabled: (enabled: boolean) => {
        dispatch({ type: 'set-comments-enabled', payload: { enabled } })
      },
      canUpdateCommentsEnabled: values.userCanUpdateCommentsEnabled,
    }),
    [values, dispatch, share, initialValues, pending, updateMemberConfirmed]
  )

  return {
    updateMemberLoading,
    getUpdateMemberDropdownProps,
    getUpdateMemberConfirmDialogProps,
    updateMemberConfirmDialogOpen: dialogOpen,
    updateMemberLevel: values.privateAccessLevel,
  }
}
